import React, { useMemo, useState, useEffect } from 'react';
import GymRowStyles from './GymRow.module.scss';
import Person4OutlinedIcon from '@mui/icons-material/Person4Outlined';
import EmptyState from '../composites/EmptyState';
import ContentDrill from '../composites/ContentDrill';
import GymRow from './GymRow';
import ListContent from '../composites/ListContent';
import { useAdminGyms } from '../../hooks/useAdminGyms';
import GymSettings from '../GymSettings/GymSettings';

import type { Gym } from '../../types';

const gymListTitles = ['Gym Name', 'Logo', 'Options'];

const Gyms: React.FC = () => {
  const { gyms: availableGyms } = useAdminGyms();

  const [selectedGym, setSelectedGym] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    if (!selectedGym) {
      return;
    }
    const exists = availableGyms.some((gym) => gym.id === selectedGym);
    if (!exists) {
      setSelectedGym(null);
    }
  }, [availableGyms, selectedGym]);

  const filteredGyms = useMemo(() => {
    if (!availableGyms?.length) return [];
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return availableGyms
      .filter((gym: Gym) => {
        if (!normalizedQuery) return true;
        return gym.name.toLowerCase().includes(normalizedQuery);
      })
      .sort(
        (a, b) =>
          a.name?.toLowerCase().localeCompare(b.name?.toLowerCase() || '') || 0,
      );
  }, [searchQuery, availableGyms]);

  const renderEmptyState = () => (
    <EmptyState icon={<Person4OutlinedIcon />} text="No Gyms Have Been Added" />
  );

  const renderContent = () => {
    return (
      <ListContent
        items={filteredGyms}
        listHeaderClassName={GymRowStyles.listGrid}
        EmptyStateComponent={renderEmptyState}
        titles={gymListTitles}
        ItemComponent={({ data }) => (
          <GymRow
            gym={data}
            selected={selectedGym === data.id}
            onSelect={() => setSelectedGym(data.id)}
            key={data.id}
          />
        )}
      />
    );
  };

  const getDrillInContent = () => {
    if (!selectedGym) return null;
    const gym = availableGyms?.find((gym) => gym.id === selectedGym);
    return (
      <GymSettings
        gym={gym}
        onClose={() => {
          setSelectedGym(null);
        }}
      />
    );
  };

  const selectedGymData = selectedGym
    ? availableGyms?.find((gym) => gym.id === selectedGym)
    : null;
  const title = selectedGymData?.name ?? 'Gyms';
  const isDrilledIn = Boolean(selectedGym);

  return (
    <ContentDrill
      title={title}
      onBack={isDrilledIn ? () => setSelectedGym(null) : undefined}
      isParent
      actions={[]}
      searchPlaceholder="Search for Gym"
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      disableSearch={false}
      isDrilledIn={isDrilledIn}
      drillInContent={getDrillInContent()}
    >
      {renderContent()}
    </ContentDrill>
  );
};

export default Gyms;
